import { useContext, useState } from "react";

import { UserSessionContext } from "../../use-user-context";

import type { UserSession } from "../../session";
import type { ApiError } from "@sperrdatei/dto/types";
import type { FormEvent } from "react";

export type LoginProps = object;

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

export const Login: React.FC<LoginProps> = () => {
  const [, setUserSession] = useContext(UserSessionContext);
  const [validated, setValidated] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<ApiError | null>(null);
  const [stayLoggedIn, setStayLoggedIn] = useState(false);
  const [passwordFieldType, setPasswordFieldType] = useState<
    "text" | "password"
  >("password");

  const isFieldValid = (field: "email" | "password"): boolean => {
    if (!validated) {
      return true;
    }

    if (field === "email" && !isValidEmail(email)) {
      return false;
    }

    if (!error) {
      return true;
    }

    return field === "email"
      ? error.code !== "UNKNOWN_USER"
      : error.code !== "WRONG_PASSWORD";
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setValidated(true);

    if (!isValidEmail(email)) {
      return;
    }

    const response = await fetch("/api/login/session", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        username: email,
        password: password,
      }),
    });

    if (response.ok) {
      response.json().then((activeSession: UserSession) => {
        setUserSession({ active: true, ...activeSession }, stayLoggedIn);
      });
    } else {
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        response.json().then((error: ApiError) => {
          setError(error);
        });
      } else {
        setError({
          code: response.status.toString(),
          message: response.statusText,
        });
      }
    }
  };

  const togglePasswordVisibility = (): void => {
    if (passwordFieldType === "password") {
      setPasswordFieldType("text");
    } else {
      setPasswordFieldType("password");
    }
  };

  return (
    <div className="login container d-flex justify-content-center align-items-center h-100">
      <form onSubmit={handleSubmit} className="container border p-4" noValidate>
        {error &&
          error.code !== "UNKNOWN_USER" &&
          error.code !== "WRONG_PASSWORD" && (
            <div className="alert alert-danger mb-3" role="alert">
              <h6 className="alert-heading small">
                <i className="bi bi-exclamation-triangle-fill" /> Fehler
              </h6>
              <span className="small">{error.message}</span>
            </div>
          )}
        <div className="mb-3">
          <label htmlFor="email" className="form-text">
            Email
          </label>
          <input
            type="email"
            id="email"
            value={email}
            className={
              isFieldValid("email") ? "form-control" : "form-control is-invalid"
            }
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <div id="emailFeedback" className="invalid-feedback">
            {!email
              ? "Angabe notwendig"
              : !isValidEmail(email)
                ? "Ungültige E-Mail Adresse"
                : error?.message}
          </div>
        </div>
        <div className="mb-3">
          <label htmlFor="password" className="form-text">
            Passwort
          </label>
          <div className="input-group">
            <input
              type={passwordFieldType}
              id="password"
              value={password}
              className={
                isFieldValid("password")
                  ? "form-control"
                  : "form-control is-invalid"
              }
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <span
              className="input-group-text "
              role="button"
              title={
                passwordFieldType === "text"
                  ? "passwort verstecken"
                  : "passwort anzeigen"
              }
            >
              <i
                className={
                  passwordFieldType === "text" ? "bi bi-eye-slash" : "bi bi-eye"
                }
                onClick={togglePasswordVisibility}
              ></i>
            </span>
            <div id="passwordFeedback" className="invalid-feedback">
              {!password ? "Angabe notwendig" : error?.message}
            </div>
          </div>
        </div>
        <div className="mb-4 form-check d-none">
          <input
            type="checkbox"
            className="form-check-input"
            id="staySignedIn"
            onChange={(e) => setStayLoggedIn(e.target.checked)}
          />
          <label className="form-check-label form-text" htmlFor="staySignedIn">
            angemeldet bleiben
          </label>
        </div>
        <button
          type="submit"
          className="btn btn-primary mt-2"
          disabled={!email || !password}
        >
          Anmelden
        </button>
      </form>
    </div>
  );
};
